import React, { useCallback, useEffect, useState } from 'react';
import Card from '../../components/elements/Card';
import * as Unicons from '@iconscout/react-unicons';
import { useHistory, useLocation } from 'react-router-dom';
import AdminLayout from '../../components/layout/admin';
import Breadcrumbs from '../../components/elements/Breadcrumbs';
import ToolBox from '../../components/elements/ToolBox';
import DataTables from '../../components/elements/DataTables';
import { renderDate } from '../../utilities/renderDate';
import { useMutation, useQuery } from '@tanstack/react-query';
import { showNotification } from '../../utilities/mantineNotification';
import {
  GetInventoryReward,
  deleteInventoryReward,
  editInventoryReward,
  postInventoryReward
} from '../../services/inventoryReward';
import CreateInventoryReward from './CreateInventoryReward';
import { getSharepointCdnFileMedia } from '../../utilities';
import { Image, NumberFormatter, Popover } from '@mantine/core';
import ModalDelete from '../../components/fragments/ModalDelete/ModalDelete';
import LabelDate from '../../components/fragments/LabelDate';

const columns = [
  {
    heading: 'Inventory name Reward',
    props: 'inventoryName',
    id: 'inventoryName',
    sortable: true
  },
  {
    heading: 'Reward Image',
    props: (item) => renderImage(item),
    id: 'rewardImage',
    sortable: true
  },

  {
    heading: 'Redeem Point',
    props: (item) => renderFormatNumber(item, 'redeemPoint', ' point'),
    id: 'redeemPoint',
    sortable: true
  },
  {
    heading: 'Total Stock',
    props: (item) => renderFormatNumber(item, 'stockReward', ' stock'),
    id: 'stockReward',
    sortable: true
  },
  {
    heading: 'Maximum Redeem per User',
    props: (item) => renderFormatNumber(item, 'maxRedeem', 'x'),
    id: 'maxRedeem',
    sortable: true
  },
  {
    heading: 'Available Stock',
    props: (item) => renderFormatNumber(item, 'availableStock', ' stock'),
    id: 'availableStock',
    sortable: true
  },
  {
    heading: 'Redeemed Stock',
    props: (item) => renderFormatNumber(item, 'redeemStock', ' stock'),
    id: 'redeemStock',
    sortable: true
  },
  {
    heading: 'Expired At',
    props: (item) => <LabelDate date={item.endDate} />,
    id: 'endDate',
    sortable: true
  },
  {
    heading: 'Last Update By',
    props: 'userUp',
    id: 'userUp',
    sortable: true
  },
  {
    heading: 'Modified Date',
    props: (item) => <LabelDate date={item.dateUp} />,
    id: 'dateUp',
    sortable: true
  },
  {
    heading: 'Action',
    props: 'action',
    sortable: false
  }
];

const renderFormatNumber = (item, props, suffix) => {
  return (
    <NumberFormatter
      value={Number(item[props])}
      suffix={suffix}
      thousandSeparator="."
      decimalSeparator=","
    />
  );
};

const renderImage = (item) => {
  return (
    <Popover width={200} position="bottom" withArrow shadow="md">
      <Popover.Target>
        <Image
          src={getSharepointCdnFileMedia(item.rewardImage)}
          w={120}
          radius="md"
          style={{ cursor: 'pointer' }}
        />
      </Popover.Target>

      <Popover.Dropdown>
        <Image
          src={getSharepointCdnFileMedia(item.rewardImage)}
          width={'100%'}
        />
      </Popover.Dropdown>
    </Popover>
  );
};

const InventoryRewardPage = (props) => {
  const history = useHistory();
  const location = useLocation();

  const queryParams: any = new URLSearchParams(location.search);

  const paramsObject = {
    search: queryParams.get('search'),
    page: queryParams.get('page'),
    size: queryParams.get('size')
  };

  const [modalConfig, setModalConfig] = useState({
    show: false,
    data: null,
    type: ''
  });

  const [filter, setFilter] = useState({
    search: '',
    page: 1,
    size: 10
  });

  // Queries
  const fetchedInventoryReward = useQuery({
    queryKey: ['getInventoryReward'],
    queryFn: () =>
      GetInventoryReward({
        search: filter.search,
        page: filter.page,
        size: filter.size
      })
  });

  const createInventoryReward = useMutation({
    mutationFn: postInventoryReward,
    onSuccess: () => {
      fetchedInventoryReward.refetch();
      handleCloseModal();
      showNotification({
        type: 'success'
      });
    },
    onError: (error) => {
      showNotification({
        type: 'error',
        message: JSON.stringify(error)
      });
    }
  });

  const updateMasterCategory = useMutation({
    mutationFn: editInventoryReward,
    onSuccess: () => {
      fetchedInventoryReward.refetch();
      handleCloseModal();
      showNotification({
        type: 'success',
        title: 'Success Update Master Level',
        message: 'Updated data will be show in the table'
      });
    },
    onError: (error) => {
      showNotification({
        type: 'error',
        message: JSON.stringify(error)
      });
    }
  });

  const removeInventoryReward = useMutation({
    mutationFn: deleteInventoryReward,
    onSuccess: () => {
      fetchedInventoryReward.refetch();
      handleCloseModal();
      showNotification({
        type: 'success',
        title: 'Success Delete Master Level',
        message: 'Delete Master Level success'
      });
    },
    onError: (error) => {
      showNotification({
        type: 'error',
        message: JSON.stringify(error)
      });
    }
  });

  useEffect(() => {
    if (filter?.search || filter?.page || filter?.size) {
      fetchedInventoryReward.refetch();
    }
  }, [filter]);

  useEffect(() => {
    if (paramsObject?.search || paramsObject?.page || paramsObject?.size) {
      setFilter({
        search: paramsObject?.search || '',
        page: paramsObject?.page || 1,
        size: paramsObject?.size || 10
      });
    }
  }, [paramsObject?.search, paramsObject?.page, paramsObject?.size]);

  const buttonOnClick = () => {
    setModalConfig({
      show: true,
      data: null,
      type: 'add'
    });
  };

  const handleSearch = useCallback((data: any) => {
    history.push({
      pathname: '/management/inventory-reward',
      search: `?search=${data}&page=1`
    });
  }, []);

  const handleCloseModal = () => {
    setModalConfig({
      show: false,
      data: null,
      type: ''
    });
  };

  const handleDeleteData = (data) => removeInventoryReward.mutate(data);

  const handleSubmitData = (data) => {
    const payload = {
      ...data,
      userIn:
        modalConfig.type === 'edit'
          ? modalConfig.data.userIn
          : props.auth.binusianId,
      userUp: modalConfig.type === 'edit' ? props.auth.binusianId : ''
    };

    if (modalConfig.type === 'edit') {
      updateMasterCategory.mutate({
        ...payload,
        inventoryRewardID: modalConfig.data.inventoryRewardID
      });
      return;
    }
    createInventoryReward.mutate(payload);
    return;
  };

  return (
    <AdminLayout>
      <Breadcrumbs
        pageTitle="Inventory Reward"
        itemList={[
          {
            title: 'Back Office BCL&D',
            href: '#',
            icon: <Unicons.UilFileCopyAlt size={16} />
          },
          {
            title: 'Reward & Gamification',
            href: '#'
          },
          {
            title: 'Inventory Reward',
            href: '/management/inventory-reward'
          }
        ]}
      />
      <Card>
        <ToolBox
          handleSearch={handleSearch}
          buttonOnClick={buttonOnClick}
          buttonText="Create Inventory Reward"
        />
        <DataTables
          columns={columns}
          loading={fetchedInventoryReward?.isLoading}
          data={fetchedInventoryReward?.data?.data?.data || []}
          handleDelete={(data) => {
            setModalConfig({
              show: true,
              data: data,
              type: 'delete'
            });
          }}
          handleEdit={(data) => {
            setModalConfig({
              show: true,
              data: data,
              type: 'edit'
            });
          }}
        />
      </Card>

      <CreateInventoryReward
        isVisible={
          modalConfig.show &&
          (modalConfig.type === 'add' || modalConfig.type === 'edit')
        }
        type={modalConfig.type}
        data={modalConfig.data}
        onClose={handleCloseModal}
        onSubmit={handleSubmitData}
        loading={
          createInventoryReward.isLoading || updateMasterCategory.isLoading
        }
      />

      <ModalDelete
        title="Inventory Reward"
        isVisible={modalConfig.show && modalConfig.type === 'delete'}
        loading={removeInventoryReward.isLoading}
        onClose={handleCloseModal}
        onConfirmation={() =>
          handleDeleteData({
            inventoryRewardID: modalConfig.data.inventoryRewardID,
            userUp: props.auth.binusianId
          })
        }
      />
    </AdminLayout>
  );
};

export default InventoryRewardPage;
